// frontend/src/features/livestock/CompareSideBySide.tsx
// Two tank shots next to each other (design 7.2): the screen-reader-friendly
// view, stacked on a phone held upright. Both sides share one zoom
// (docs/plans/2026-10-29-tank-shot-framing-design.md, 9): pinch or ctrl+wheel
// on either zooms both; while zoomed, a mouse drag (or a finger in full
// screen) pans both. With independentZoom ("As taken"), each side zooms and
// pans on its own, with its own zoom buttons under it.

import { useRef, useState } from 'react';
import { SimpleGrid, Box, Stack } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';
import { formatPhotoDay } from './photoInboxFormat';
import { CompareImage, type CompareShotImage } from './CompareImage';
import { NO_ZOOM, zoomTransform, type CompareZoom } from './compareZoom';
import { useZoomGestures } from './useZoomGestures';
import { ZoomControls } from './ZoomControls';
import classes from './Compare.module.css';

export interface CompareSideBySideProps {
  before: CompareShotImage;
  after: CompareShotImage;
  fullScreen?: boolean;
  /** The synced zoom, when the parent holds it. */
  zoom?: CompareZoom;
  onZoomChange?: (zoom: CompareZoom) => void;
  onImageError?: () => void;
  /** Starts each image's alt text: "Tank shot before, 3 Mar 2026". */
  nounTitle?: string;
  /** Each side zooms on its own (zoom and onZoomChange are then unused). */
  independentZoom?: boolean;
}

interface SideFrameProps {
  side: 'before' | 'after';
  label: string;
  image: CompareShotImage;
  fullScreen: boolean;
  zoom: CompareZoom;
  onZoomChange: (zoom: CompareZoom) => void;
  onImageError?: () => void;
  nounTitle: string;
}

function SideFrame({
  side,
  label,
  image,
  fullScreen,
  zoom,
  onZoomChange,
  onImageError,
  nounTitle,
}: SideFrameProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const gestures = useZoomGestures({
    ref,
    zoom,
    onZoomChange,
    preventBrowserZoom: fullScreen,
    panWithOnePointer: (event) => event.pointerType === 'mouse' || fullScreen,
  });
  const day = formatPhotoDay(image.date);
  return (
    <Box
      ref={ref}
      className={classes.sideFrame}
      data-zoomed={zoom.scale > 1 || undefined}
      data-testid={`compare-side-${side}`}
      onPointerDown={(event) => gestures.pointerDown(event)}
      onPointerMove={(event) => gestures.pointerMove(event)}
      onPointerUp={(event) => gestures.pointerEnd(event, true)}
      onPointerCancel={(event) => gestures.pointerEnd(event, false)}
      onLostPointerCapture={(event) => {
        if (event.target === event.currentTarget) gestures.forget(event.pointerId);
      }}
      onPointerLeave={(event) => {
        if (!ref.current?.hasPointerCapture?.(event.pointerId)) gestures.forget(event.pointerId);
      }}
      onDragStart={(event) => event.preventDefault()}
    >
      <Box
        className={classes.zoomLayer}
        style={zoomTransform(zoom)}
        data-testid="compare-zoom-layer"
      >
        <CompareImage image={image} alt={`${nounTitle} ${side}, ${day}`} onError={onImageError} />
      </Box>
      <span className={classes.label} data-side="before">
        {label} · {day}
      </span>
    </Box>
  );
}

export function CompareSideBySide({
  before,
  after,
  fullScreen = false,
  zoom: zoomProp,
  onZoomChange,
  onImageError,
  nounTitle = 'Tank shot',
  independentZoom = false,
}: CompareSideBySideProps): JSX.Element {
  const [zoom, changeZoom] = useUncontrolled({
    value: zoomProp,
    defaultValue: NO_ZOOM,
    finalValue: NO_ZOOM,
    onChange: onZoomChange,
  });
  const [sideZooms, setSideZooms] = useState<Record<'before' | 'after', CompareZoom>>({
    before: NO_ZOOM,
    after: NO_ZOOM,
  });
  const sides = [
    { key: 'before', label: 'Before', image: before },
    { key: 'after', label: 'After', image: after },
  ] as const;
  return (
    <Box className={classes.wrap} data-fullscreen={fullScreen || undefined}>
      <SimpleGrid
        cols={{ base: 1, sm: 2 }}
        spacing={fullScreen ? 4 : { base: 'xs', sm: 'sm' }}
        w="100%"
      >
        {sides.map(({ key, label, image }) => {
          if (!independentZoom) {
            return (
              <SideFrame
                key={key}
                side={key}
                label={label}
                image={image}
                fullScreen={fullScreen}
                zoom={zoom}
                onZoomChange={changeZoom}
                onImageError={onImageError}
                nounTitle={nounTitle}
              />
            );
          }
          const changeSide = (next: CompareZoom): void =>
            setSideZooms((current) => ({ ...current, [key]: next }));
          return (
            <Stack key={key} gap={4}>
              <SideFrame
                side={key}
                label={label}
                image={image}
                fullScreen={fullScreen}
                zoom={sideZooms[key]}
                onZoomChange={changeSide}
                onImageError={onImageError}
                nounTitle={nounTitle}
              />
              <ZoomControls
                zoom={sideZooms[key]}
                onZoomChange={changeSide}
                dark={fullScreen}
                label={`Zoom ${key}`}
              />
            </Stack>
          );
        })}
      </SimpleGrid>
    </Box>
  );
}
